import type { SearchIndexEntry, RankedDomain } from '../types'

/**
 * Compute search rank for a domain entry against a query
 *
 * Implements an 8-tier ranking system:
 * - Exact id match: 1000 (highest priority)
 * - Id prefix match: 900
 * - DisplayName prefix match: 800
 * - Name prefix match: 700
 * - AccountName prefix match: 600
 * - DisplayName contains: 400
 * - Name contains: 300
 * - AccountName contains: 200
 *
 * @param entry - Search index entry with normalized fields
 * @param query - Normalized search query (lowercase)
 * @returns Rank score (0 if no match)
 */
export const computeRank = (entry: SearchIndexEntry, query: string): number => {
    const q = query.toLowerCase().trim()

    if (!q) {
        return 0
    }

    // Tier 1: Exact id match (highest priority)
    if (entry.normalizedId === q) {
        return 1000
    }

    // Tier 2: Id prefix match
    if (entry.normalizedId.startsWith(q)) {
        return 900
    }

    // Tier 3: DisplayName prefix match
    if (entry.normalizedDisplayName.startsWith(q)) {
        return 800
    }

    // Tier 4: Name prefix match
    if (entry.normalizedName.startsWith(q)) {
        return 700
    }

    // Tier 5: AccountName prefix match
    if (entry.normalizedAccountName.startsWith(q)) {
        return 600
    }

    // Tier 6: DisplayName contains
    if (entry.normalizedDisplayName.includes(q)) {
        return 400
    }

    // Tier 7: Name contains
    if (entry.normalizedName.includes(q)) {
        return 300
    }

    // Tier 8: AccountName contains
    if (entry.normalizedAccountName.includes(q)) {
        return 200
    }

    // No match
    return 0
}

/**
 * Filter and rank domains based on search query
 *
 * Implements O(n) filtering with intelligent ranking. When query is empty,
 * returns all domains with rank 0. When query is provided, filters to only
 * matching domains and sorts by rank descending, then alphabetically by
 * displayName as tiebreaker.
 *
 * @param searchIndex - Array of search index entries with normalized fields
 * @param query - Search query string
 * @returns Array of ranked domains sorted by relevance
 */
export const filterDomains = (searchIndex: SearchIndexEntry[], query: string): RankedDomain[] => {
    const trimmedQuery = query.trim()

    // Empty query: return all domains with default rank
    if (!trimmedQuery) {
        return searchIndex.map((entry) => ({
            domain: entry.domain,
            rank: 0,
        }))
    }

    // Filter and rank domains - O(n) complexity
    const results: RankedDomain[] = []

    for (const entry of searchIndex) {
        const rank = computeRank(entry, trimmedQuery)
        if (rank > 0) {
            results.push({ domain: entry.domain, rank })
        }
    }

    // Sort by rank descending, then alphabetically by displayName
    // O(m log m) where m = matched domains (typically m << n)
    results.sort((a, b) => {
        // Higher rank first
        if (a.rank !== b.rank) {
            return b.rank - a.rank
        }
        // Alphabetical tiebreaker
        return a.domain.displayName.toLowerCase().localeCompare(b.domain.displayName.toLowerCase())
    })

    return results
}
